/**
 * Client-side session and save store.
 *
 * The server is authoritative: this caches the save for the UI, mirrors it into
 * localStorage so the menus render instantly and survive a refresh while
 * offline, and pushes changes back through the API.
 */

import { createDefaultSave, levelFromXp, migrateSave, type GameSettings, type KeyBindings, type SaveData } from '@shared/save.js';
import type { RewardBreakdown } from '@shared/race.js';
import type { RaceModeId } from '@shared/net.js';
import type { AIDifficulty } from '@shared/ai.js';

export interface SessionUser {
  id: number;
  username: string;
  displayName: string;
  isAdmin: boolean;
  disabled: boolean;
  money: number;
  xp: number;
  level: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
  lastLogin: string | null;
}

export interface UserStats {
  races: number;
  wins: number;
  podiums: number;
  bestLap: number;
}

export interface RaceSubmission {
  trackId: string;
  mode: RaceModeId;
  position: number;
  racers: number;
  laps: number;
  bestLap: number;
  raceTime: number;
  driftScore: number;
  respawns: number;
  finished: boolean;
  topSpeed: number;
  difficulty: AIDifficulty;
}

export interface RaceOutcome {
  rewards: RewardBreakdown;
  newAchievements: Array<{ id: string; name: string; reward: number }>;
  personalBest: boolean;
  level: { level: number; intoLevel: number; needed: number };
}

const LOCAL_KEY = 'apogee.save.v2';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, message: string, code = 'error') {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export class Store {
  user: SessionUser | null = null;
  save: SaveData = createDefaultSave();
  stats: UserStats = { races: 0, wins: 0, podiums: 0, bestLap: 0 };
  /** True when the server could not be reached; the game still plays. */
  offline = false;
  motd = '';
  defaultAdminPassword = false;

  /** Listeners for save changes (used to refresh open menus). */
  private listeners = new Set<() => void>();

  constructor() {
    this.loadLocal();
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }

  private loadLocal(): void {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      if (raw) this.save = migrateSave(JSON.parse(raw));
    } catch {
      this.save = createDefaultSave();
    }
  }

  private persistLocal(): void {
    try {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(this.save));
    } catch {
      /* storage full or blocked — the server copy is still authoritative */
    }
  }

  get money(): number {
    return this.user ? this.user.money : this.save.money;
  }

  get xp(): number {
    return this.user ? this.user.xp : this.save.xp;
  }

  get level(): number {
    return levelFromXp(this.xp).level;
  }

  get signedIn(): boolean {
    return this.user !== null;
  }

  // ------------------------------------------------------------------ HTTP
  /** `body` is passed as a plain object and serialised here. */
  private async request<T>(
    path: string,
    init: Omit<RequestInit, 'body'> & { body?: unknown } = {},
  ): Promise<T> {
    const options: RequestInit = { credentials: 'same-origin', method: init.method, headers: init.headers };
    if (init.body !== undefined && typeof init.body !== 'string') {
      options.headers = { ...(init.headers ?? {}), 'Content-Type': 'application/json' };
      options.body = JSON.stringify(init.body);
    } else if (typeof init.body === 'string') {
      options.body = init.body;
    }
    let response: Response;
    try {
      response = await fetch(path, options);
    } catch {
      this.offline = true;
      throw new ApiError(0, 'Cannot reach the server.', 'offline');
    }
    this.offline = false;
    let payload: unknown = null;
    const text = await response.text();
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = null;
      }
    }
    if (!response.ok) {
      const body = payload as { error?: string; code?: string } | null;
      throw new ApiError(response.status, body?.error ?? `Request failed (${response.status})`, body?.code ?? 'error');
    }
    return payload as T;
  }

  async fetchBootstrap(): Promise<void> {
    try {
      const data = await this.request<{ motd: string; defaultAdminPassword: boolean }>('/api/bootstrap');
      this.motd = data.motd ?? '';
      this.defaultAdminPassword = data.defaultAdminPassword === true;
    } catch {
      this.motd = '';
    }
  }

  /** Restores an existing session cookie, if there is one. */
  async restore(): Promise<boolean> {
    try {
      const data = await this.request<{
        user: SessionUser | null;
        save?: SaveData;
        stats?: UserStats;
      }>('/api/me');
      if (!data.user || !data.save) return false;
      this.applySession(data.user, data.save, data.stats ?? { races: 0, wins: 0, podiums: 0, bestLap: 0 });
      return true;
    } catch {
      // No server, or a stale cookie: fall through to the login screen.
      return false;
    }
  }

  async login(username: string, password: string): Promise<void> {
    const data = await this.request<{ user: SessionUser; save: SaveData }>('/api/login', {
      method: 'POST',
      body: { username, password },
    });
    this.applySession(data.user, data.save, { races: 0, wins: 0, podiums: 0, bestLap: 0 });
    // Pull the richer payload (stats, revision) straight after signing in.
    await this.restore();
  }

  async logout(): Promise<void> {
    try {
      await this.request('/api/logout', { method: 'POST', body: {} });
    } catch {
      /* signing out locally is enough if the server is unreachable */
    }
    this.user = null;
    this.save = createDefaultSave();
    this.persistLocal();
    this.notify();
  }

  private applySession(user: SessionUser, save: SaveData, stats: UserStats): void {
    this.user = user;
    this.save = migrateSave(save);
    // Currency and XP are owned by the user row on the server.
    this.save.money = user.money;
    this.save.xp = user.xp;
    this.stats = stats;
    this.persistLocal();
    this.notify();
  }

  /** Re-reads the save from the server (used after a race or a purchase). */
  async refresh(): Promise<void> {
    const data = await this.request<{ user: SessionUser; save: SaveData; stats: UserStats }>('/api/me');
    this.applySession(data.user, data.save, data.stats);
  }

  /** Persists purely-local preferences quickly, then pushes them upstream. */
  async pushSave(): Promise<void> {
    this.persistLocal();
    if (!this.user) return;
    try {
      const result = await this.request<{ save: SaveData }>('/api/save', { method: 'PUT', body: { save: this.save } });
      this.save = migrateSave(result.save);
      this.persistLocal();
      this.notify();
    } catch {
      this.offline = true;
    }
  }

  async buyCar(carId: string): Promise<void> {
    const result = await this.request<{ save: SaveData; money: number }>('/api/garage/buy-car', {
      method: 'POST',
      body: { carId },
    });
    this.save = migrateSave(result.save);
    if (this.user) this.user.money = result.money;
    this.save.money = result.money;
    this.persistLocal();
    this.notify();
  }

  async buyUpgrade(carId: string, upgradeId: string): Promise<number> {
    const result = await this.request<{ save: SaveData; money: number; level: number }>('/api/garage/buy-upgrade', {
      method: 'POST',
      body: { carId, upgradeId },
    });
    this.save = migrateSave(result.save);
    if (this.user) this.user.money = result.money;
    this.save.money = result.money;
    this.persistLocal();
    this.notify();
    return result.level;
  }

  async submitRace(submission: RaceSubmission): Promise<RaceOutcome | null> {
    try {
      const result = await this.request<{
        user: SessionUser;
        save: SaveData;
        stats?: UserStats;
        rewards: RewardBreakdown;
        newAchievements: Array<{ id: string; name: string; reward: number }>;
        personalBest: boolean;
        level: { level: number; intoLevel: number; needed: number };
      }>('/api/races', { method: 'POST', body: submission });
      this.applySession(result.user, result.save, result.stats ?? this.stats);
      return {
        rewards: result.rewards,
        newAchievements: result.newAchievements,
        personalBest: result.personalBest,
        level: result.level,
      };
    } catch (err) {
      // Offline: keep the local best-lap records so practice still feels useful.
      this.applyLocalRace(submission);
      if (err instanceof ApiError && err.status === 0) return null;
      throw err;
    }
  }

  /**
   * Applies the parts of a result that are safe to keep locally. No currency is
   * awarded — money only ever comes from the server.
   */
  private applyLocalRace(submission: RaceSubmission): void {
    const prev = this.save.bestLaps[submission.trackId] ?? 0;
    if (submission.bestLap > 0 && (prev <= 0 || submission.bestLap < prev)) {
      this.save.bestLaps[submission.trackId] = Number(submission.bestLap.toFixed(3));
    }
    this.save.stats.races += 1;
    this.save.stats.driftScore += submission.driftScore;
    this.save.lastTrack = submission.trackId;
    this.save.lastMode = submission.mode;
    this.persistLocal();
    this.notify();
  }

  async leaderboard(trackId: string): Promise<Array<{ displayName: string; bestLap: number; position: number }>> {
    const data = await this.request<{ entries: Array<{ displayName: string; bestLap: number; position: number }> }>(
      `/api/leaderboard?track=${encodeURIComponent(trackId)}`,
    );
    return data.entries;
  }

  async recentRaces(): Promise<Array<Record<string, unknown>>> {
    const data = await this.request<{ races: Array<Record<string, unknown>> }>('/api/races/recent');
    return data.races;
  }

  async changePassword(current: string, next: string): Promise<void> {
    await this.request('/api/me/password', { method: 'POST', body: { current, next } });
  }

  // -------------------------------------------------------------- settings
  get settings(): GameSettings {
    return this.save.settings;
  }

  get controls(): KeyBindings {
    return this.save.controls;
  }

  updateSettings(patch: Partial<GameSettings>): void {
    this.save.settings = { ...this.save.settings, ...patch };
    this.persistLocal();
    this.notify();
  }

  updateControls(patch: Partial<KeyBindings>): void {
    this.save.controls = { ...this.save.controls, ...patch };
    this.persistLocal();
    this.notify();
  }

  selectCar(carId: string): void {
    if (!this.save.cars.includes(carId)) return;
    this.save.selectedCar = carId;
    this.persistLocal();
    void this.pushSave();
    this.notify();
  }

  setPaint(carId: string, colour: string): void {
    this.save.paint = { ...this.save.paint, [carId]: colour };
    this.persistLocal();
    void this.pushSave();
    this.notify();
  }

  rememberLastChoice(trackId: string, mode: RaceModeId): void {
    this.save.lastTrack = trackId;
    this.save.lastMode = mode;
    this.persistLocal();
  }
}
